import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { TrendingUp, Users, DollarSign, FileText, BarChart3 } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { formatCurrency as marketFormatCurrency } from '@/lib/market';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, config),
        axios.get(`${API}/admin/all-transactions`, config)
      ]);

      setStats(statsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => marketFormatCurrency(value, user?.market);

  // AUM Growth Chart
  const getAUMGrowthChart = () => {
    if (!stats?.aum_history) return {};

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ffd87a',
        borderWidth: 1,
        textStyle: { color: '#1f2937' }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: stats.aum_history.map(h => h.month),
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { color: '#6b7280' }
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { 
          color: '#6b7280',
          formatter: (value) => formatCurrency(value)
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [
        {
          data: stats.aum_history.map(h => h.value),
          type: 'line',
          smooth: true,
          lineStyle: { color: '#ffd87a', width: 3 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(255, 216, 122, 0.3)' },
                { offset: 1, color: 'rgba(255, 216, 122, 0.05)' }
              ]
            }
          },
          itemStyle: { color: '#ffd87a' }
        }
      ]
    };
  };

  // RM Performance Chart
  const getRMPerformanceChart = () => {
    if (!stats?.rm_performance) return {};

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ffd87a',
        borderWidth: 1,
        textStyle: { color: '#1f2937' }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: stats.rm_performance.map(rm => rm.rm_name.split(' ')[0]),
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { color: '#6b7280' }
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { 
          color: '#6b7280',
          formatter: '{value}%'
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [
        {
          name: 'Profit %',
          type: 'bar',
          data: stats.rm_performance.map(rm => rm.profit_percentage.toFixed(2)),
          itemStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#ffd87a' },
                { offset: 1, color: '#f5cc6a' }
              ]
            },
            borderRadius: [4, 4, 0, 0]
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-1" style={{fontFamily: "'PerfectNineties', serif"}}>
            Admin Dashboard
          </h1>
          <p className="text-sm text-gray-600">Platform-wide analytics and performance metrics</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 stagger-in">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <DollarSign size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total AUM</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.total_aum || 0)}</p>
              </div>
            </div>
            <p className="text-xs text-ugmonk-dark font-medium">
              +{((stats?.total_gains / stats?.total_aum * 100) || 0).toFixed(2)}% Total Gain
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <Users size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Clients</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_clients || 0}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500">Across {stats?.total_rms || 0} RMs</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <TrendingUp size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Gains</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.total_gains || 0)}</p>
              </div>
            </div>
            <p className="text-xs text-ugmonk-dark font-medium">Portfolio Performance</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border card-glow">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <FileText size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Reports Generated</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_reports || 0}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500">By all RMs</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AUM Growth Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 size={20} className="text-ugmonk-dark" />
              Total AUM Growth
            </h3>
            <ReactECharts 
              option={getAUMGrowthChart()} 
              style={{ height: '300px' }}
              opts={{ renderer: 'svg' }}
            />
          </div>

          {/* RM Performance Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp size={20} className="text-ugmonk-dark" />
              RM Performance (Profit %)
            </h3>
            <ReactECharts 
              option={getRMPerformanceChart()} 
              style={{ height: '300px' }}
              opts={{ renderer: 'svg' }}
            />
          </div>
        </div>

        {/* RM Performance Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">RM Performance Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">RM Name</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Clients</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Total AUM</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Total Gains</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Profit %</th>
                </tr>
              </thead>
              <tbody>
                {stats?.rm_performance.map((rm) => (
                  <tr key={rm.rm_id} className="border-b border-gray-100 hover:bg-[#fffdf0] transition-colors">
                    <td className="py-3 text-sm text-gray-900 font-medium">{rm.rm_name}</td>
                    <td className="py-3 text-sm text-gray-700">{rm.client_count}</td>
                    <td className="py-3 text-sm text-gray-900 font-semibold">{formatCurrency(rm.total_aum)}</td>
                    <td className="py-3 text-sm text-ugmonk-dark font-semibold">{formatCurrency(rm.total_gains)}</td>
                    <td className="py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#fffdf0] text-ugmonk-dark">
                        {rm.profit_percentage.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Date</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Client</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Security</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Quantity</th>
                  <th className="text-left text-xs font-semibold text-gray-600 pb-3 uppercase tracking-wide">Value</th>
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 10).map((txn, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-[#fffdf0] transition-colors">
                    <td className="py-3 text-xs text-gray-500">
                      {new Date(txn.date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="py-3 text-sm text-gray-900">{txn.user_name}</td>
                    <td className="py-3 text-sm text-gray-700">{txn.security}</td>
                    <td className="py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#fffdf0] text-ugmonk-dark">
                        {txn.type}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-700">{txn.quantity}</td>
                    <td className="py-3 text-sm text-gray-900 font-semibold">{formatCurrency(txn.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
